import { useRef, useState } from "react";
import { Botao, Campo, Aviso, Cartao } from "../ui";
import {
  useConsultaEmail,
  useContatos,
  useReenviarContato,
  useSalvarContato,
  type ContatoDeAcesso,
} from "../../nucleo/contatosDeAcesso";
import { formatarInstante } from "../../nucleo/cofre";

export function EscolhaDoEmail({
  email,
  cnpj,
  vincular,
  aoVincular,
  aoCorrigir,
}: {
  email: string;
  cnpj: string;
  vincular: boolean;
  aoVincular: (valor: boolean) => void;
  aoCorrigir: () => void;
}) {
  const consulta = useConsultaEmail(email, cnpj);
  return (
    <div aria-live="polite">
      {consulta.consultando ? <p role="status">Conferindo o e-mail…</p> : null}
      {consulta.isError ? (
        <Aviso tom="erro">
          Não foi possível conferir o e-mail.{" "}
          <Botao variante="fantasma" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Botao>
        </Aviso>
      ) : null}
      {consulta.data ? (
        <>
          <p>{consulta.data.mensagem}</p>
          {consulta.data.situacao === "existente" ? (
            <label className="flex gap-2 items-center min-h-11 mt-2">
              <input
                type="checkbox"
                checked={vincular}
                onChange={(e) => aoVincular(e.target.checked)}
              />
              Vincular este usuário a esta implantação
            </label>
          ) : null}
          {["existente", "outra_empresa", "inativo"].includes(
            consulta.data.situacao,
          ) ? (
            <Botao variante="fantasma" onClick={aoCorrigir}>
              Informar outro e-mail
            </Botao>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const SITUACAO: Record<string, string> = {
  pendente: "Convite pendente",
  expirado: "Convite expirado",
  aceito: "Acesso aceito",
  revogado: "Acesso revogado",
  sem_convite: "Sem convite",
};

export function ContatosDeAcesso({
  id,
  cnpj,
  podeVer,
  podeEditar,
}: {
  id: string;
  cnpj: string;
  podeVer: boolean;
  podeEditar: boolean;
}) {
  const lista = useContatos(id, podeVer);
  const reenviar = useReenviarContato(id);
  const [edicao, setEdicao] = useState<ContatoDeAcesso | "novo" | null>(null);
  const [aviso, setAviso] = useState("");
  const acionador = useRef<HTMLButtonElement | null>(null);
  const incluir = useRef<HTMLButtonElement | null>(null);
  return (
    <section className="mb-6" aria-labelledby="titulo-contatos-acesso">
      <h2 id="titulo-contatos-acesso" className="titulo-secao">
        Contatos de acesso
      </h2>
      <p className="lead">
        Gerencie quem acompanha a implantação. Os dados do contrato continuam
        somente leitura.
      </p>
      {!podeVer ? (
        <Aviso>
          Somente a equipe interna pode consultar os contatos de acesso.
        </Aviso>
      ) : (
        <>
          {lista.isPending ? (
            <p role="status">Carregando os contatos…</p>
          ) : null}
          {lista.isError ? (
            <Aviso tom="erro">
              Não foi possível carregar os contatos.{" "}
              <Botao onClick={() => void lista.refetch()}>Tentar de novo</Botao>
            </Aviso>
          ) : null}
          {aviso ? <p role="status">{aviso}</p> : null}
          {reenviar.isError ? (
            <Aviso tom="erro">
              {reenviar.error.message} Tente reenviar novamente.
            </Aviso>
          ) : null}
          {lista.data?.contatos.length === 0 ? (
            <p>
              Nenhum contato vinculado. Inclua um responsável para enviar o
              acesso.
            </p>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            {lista.data?.contatos.map((contato) => (
              <Cartao
                key={contato.id}
                titulo={contato.nome}
                className="min-w-0 break-words"
              >
                <p className="break-all">{contato.email}</p>
                <p>
                  {contato.cargo ?? "Cargo não informado"} · WhatsApp:{" "}
                  {contato.telefone ?? "Não informado"}
                </p>
                <p>{SITUACAO[contato.situacao] ?? contato.situacao}</p>
                {contato.situacao === "aceito" && !contato.gerado_em ? (
                  <p>Acesso existente: nenhum novo convite é necessário.</p>
                ) : (
                  <p>
                    Convite enviado em:{" "}
                    {formatarInstante(contato.enviado_em) ?? "Ainda não enviado"}
                  </p>
                )}
                {contato.situacao !== "aceito" && contato.envio === "falhou" ? (
                  <Aviso tom="erro">
                    O envio do convite falhou. Confira o e-mail e use Reenviar
                    convite.
                  </Aviso>
                ) : null}
                {contato.situacao !== "aceito" && contato.envio === "pendente" ? (
                  <p role="status">Envio do convite em andamento…</p>
                ) : null}
                {contato.aviso_envio ? (
                  <p>
                    Último aviso enviado em:{" "}
                    {formatarInstante(contato.aviso_enviado_em ?? null) ?? "Ainda não enviado"}
                  </p>
                ) : null}
                {contato.aviso_envio === "falhou" ? (
                  <Aviso tom="erro">
                    O envio do aviso falhou. Confira o e-mail e use Reenviar aviso de acesso.
                  </Aviso>
                ) : null}
                {contato.aviso_envio === "pendente" ? (
                  <p role="status">Envio do aviso em andamento…</p>
                ) : null}
                <p>
                  Expira em:{" "}
                  {formatarInstante(contato.expira_em) ?? "Não se aplica"}
                </p>
                {contato.aceito_em ? (
                  <p>Aceito em: {formatarInstante(contato.aceito_em)}</p>
                ) : null}
                {podeEditar ? (
                  <div className="flex gap-2 flex-wrap mt-3">
                    <Botao
                      variante="fantasma"
                      onClick={(e) => {
                        acionador.current = e.currentTarget;
                        setEdicao(contato);
                        setAviso("");
                      }}
                    >
                      Corrigir contato
                    </Botao>
                    <Botao
                      disabled={reenviar.isPending}
                      onClick={() =>
                        reenviar.mutate(contato.id, {
                          onSuccess: () =>
                            setAviso(
                              contato.situacao === "aceito"
                                ? "Envio do aviso de acesso solicitado. A senha atual continua valendo."
                                : "Novo convite gerado e envio solicitado. O link anterior foi invalidado.",
                            ),
                        })
                      }
                    >
                      {reenviar.isPending && reenviar.variables === contato.id
                        ? "Enviando…"
                        : contato.situacao === "aceito"
                          ? "Reenviar aviso de acesso"
                          : "Reenviar convite"}
                    </Botao>
                  </div>
                ) : null}
              </Cartao>
            ))}
          </div>
          {!podeEditar ? (
            <Aviso>
              Você pode consultar os contatos. A edição exige permissão da
              administração.
            </Aviso>
          ) : (
            <>
              {edicao === null ? (
                <Botao
                  ref={incluir}
                  className="mt-4"
                  onClick={() => {
                    acionador.current = null;
                    setEdicao("novo");
                  }}
                >
                  Incluir responsável
                </Botao>
              ) : (
                <FormularioDoContato
                  key={typeof edicao === "string" ? "novo" : edicao.id}
                  id={id}
                  cnpj={cnpj}
                  contato={edicao === "novo" ? undefined : edicao}
                  aoFechar={(salvou) => {
                    setEdicao(null);
                    setTimeout(
                      () => (acionador.current ?? incluir.current)?.focus(),
                      0,
                    );
                    if (salvou)
                      setAviso(
                        "Contato salvo. Confira a situação do acesso no cartão.",
                      );
                  }}
                />
              )}
            </>
          )}
          {lista.data?.historico.length ? (
            <details className="mt-4">
              <summary className="py-2">Histórico dos contatos</summary>
              <ul className="break-words">
                {lista.data.historico.map((e, i) => (
                  <li key={i}>
                    {formatarInstante(e.ocorrido_em)} · {e.ator} ·{" "}
                    {e.acao === "contato:alterado"
                      ? "Contato alterado"
                      : e.acao === "contato:convite_reenviado"
                        ? "Acesso reenviado"
                        : "Contato incluído"}
                    {typeof e.detalhe.email_anterior === "string"
                      ? ` · ${e.detalhe.email_anterior} → ${String(e.detalhe.email)}`
                      : ` · ${String(e.detalhe.email)}`}
                    {typeof e.detalhe.anterior === "object" &&
                    e.detalhe.anterior !== null ? (
                      <span>
                        {" "}
                        · Dados anteriores:{" "}
                        {Object.entries(e.detalhe.anterior)
                          .map(
                            ([campo, valor]) =>
                              `${campo === "telefone" ? "WhatsApp" : campo === "cargo" ? "Cargo" : "Nome"}: ${String(valor ?? "Não informado")}`,
                          )
                          .join("; ")}{" "}
                        · Dados atuais: {String(e.detalhe.nome ?? "")} · Cargo:{" "}
                        {String(e.detalhe.cargo ?? "Não informado")} · WhatsApp:{" "}
                        {String(e.detalhe.telefone ?? "Não informado")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      )}
    </section>
  );
}

function FormularioDoContato({
  id,
  cnpj,
  contato,
  aoFechar,
}: {
  id: string;
  cnpj: string;
  contato?: ContatoDeAcesso;
  aoFechar: (salvou: boolean) => void;
}) {
  const [nome, setNome] = useState(contato?.nome ?? "");
  const [cargo, setCargo] = useState(contato?.cargo ?? "");
  const [email, setEmail] = useState(contato?.email ?? "");
  const [telefone, setTelefone] = useState(contato?.telefone ?? "");
  const [vincular, setVincular] = useState(false);
  const [erro, setErro] = useState("");
  const [erroNome, setErroNome] = useState("");
  const nomeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [erroEmail, setErroEmail] = useState("");
  const salvar = useSalvarContato(id);
  const consulta = useConsultaEmail(email, cnpj);
  const bloqueado =
    consulta.consultando ||
    consulta.isError ||
    ["outra_empresa", "inativo"].includes(consulta.data?.situacao ?? "") ||
    (consulta.data?.situacao === "existente" &&
      !vincular &&
      email.trim().toLowerCase() !== contato?.email.toLowerCase());
  return (
    <form
      noValidate
      className="mt-4"
      aria-label="Dados do contato"
      onSubmit={(e) => {
        e.preventDefault();
        const faltaNome = !nome.trim();
        const emailIncompleto = !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(
          email.trim(),
        );
        setErroNome(faltaNome ? "Informe o nome do responsável." : "");
        setErroEmail(
          emailIncompleto
            ? "Informe um e-mail completo, como nome@empresa.com.br."
            : "",
        );
        if (faltaNome || emailIncompleto) {
          setErro("Confira os campos indicados antes de salvar.");
          return;
        }
        setErro("");
        salvar.mutate(
          {
            contatoId: contato?.id,
            dados: {
              nome: nome.trim(),
              cargo: cargo.trim() || null,
              email: email.trim(),
              telefone: telefone.trim() || null,
              vincular_existente: vincular,
            },
          },
          { onSuccess: () => aoFechar(true) },
        );
      }}
    >
      <h3 className="titulo-secao">
        {contato ? "Corrigir contato de acesso" : "Incluir responsável"}
      </h3>
      {erro || salvar.isError ? (
        <Aviso tom="erro">
          {erro || salvar.error?.message}
          {erroNome ? (
            <p>
              <a href="#titulo-contatos-acesso" onClick={(e) => { e.preventDefault(); nomeRef.current?.focus(); }}>{erroNome}</a>
            </p>
          ) : null}
          {erroEmail ? (
            <p>
              <a href="#titulo-contatos-acesso" onClick={(e) => { e.preventDefault(); emailRef.current?.focus(); }}>{erroEmail}</a>
            </p>
          ) : null}
        </Aviso>
      ) : null}
      <Campo
        ref={nomeRef}
        erro={erroNome}
        onBlur={() =>
          setErroNome(nome.trim() ? "" : "Informe o nome do responsável.")
        }
        rotulo="Nome do responsável"
        value={nome}
        autoComplete="name"
        onChange={(e) => {
          setNome(e.target.value);
          setErro("");
          setErroNome("");
        }}
        required
        maxLength={200}
        autoFocus
      />
      <Campo
        rotulo="Cargo"
        value={cargo}
        autoComplete="organization-title"
        onChange={(e) => setCargo(e.target.value)}
        maxLength={120}
      />
      <Campo
        ref={emailRef}
        erro={erroEmail}
        onBlur={() =>
          setErroEmail(
            /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
              ? ""
              : "Informe um e-mail completo, como nome@empresa.com.br.",
          )
        }
        rotulo="E-mail do responsável"
        value={email}
        type="email"
        autoComplete="email"
        onChange={(e) => {
          setEmail(e.target.value);
          setVincular(false);
          setErro("");
          setErroEmail("");
        }}
        required
        maxLength={254}
      />
      <EscolhaDoEmail
        email={email}
        cnpj={cnpj}
        vincular={vincular}
        aoVincular={setVincular}
        aoCorrigir={() => {
          setEmail("");
          setVincular(false);
        }}
      />
      <Campo
        rotulo="WhatsApp"
        value={telefone}
        type="tel"
        autoComplete="tel"
        onChange={(e) => setTelefone(e.target.value)}
        maxLength={40}
      />
      <div className="flex gap-2 mt-4">
        <Botao type="submit" disabled={salvar.isPending || bloqueado}>
          {salvar.isPending ? "Salvando…" : "Salvar contato"}
        </Botao>
        <Botao
          variante="fantasma"
          disabled={salvar.isPending}
          onClick={() => aoFechar(false)}
        >
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
